<template>
  <base-modal
    :eager="true"
    :ref="modal.key"
    :show="open"
    :title="modal.title"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
  >
      <VueDropzone v-if="dropzoneOptions.url"
                   :hidden="isEditMode"
                   id="dropZoneAttachmentModal"
                   :ref="dropZoneKey"
                   :options="dropzoneOptions"
                   @vdropzone-success="onFileSuccess"
                   @vdropzone-file-added="onFileAdded"
                   @vdropzone-error="onFileError"
      ></VueDropzone>

      <v-card outlined v-for="(attachment, index) in attachments" :key="index" class="mb-1" :loading="attachment.isLoading">
          <v-card-subtitle class="text-subtitle-1 d-flex" v-if="!isEditMode">
                  <div class="mr-auto align-self-center">
                      <v-icon small color="light-blue lighten-4" class="pa-0">mdi-file</v-icon>
                      {{ attachment.fileName }} ({{ attachment.fileSize }} bytes)
                  </div>
                  <div>
                      <v-icon icon color="red" @click="remove(index)">mdi-delete</v-icon>
                  </div>
          </v-card-subtitle>
          <v-card-text class="p-0 m-0">
              <v-text-field
                  v-model="attachment.description"
                  v-bind:label="$t('Description')"
                  :error-messages="getDescriptionErrors(index)"
                  @input="$v.attachments.$each[index].description.$touch()"
                  @blur="$v.attachments.$each[index].description.$touch()">
                  ></v-text-field>
          </v-card-text>
      </v-card>
  </base-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import AttachmentService from '@/services/AttachmentService';
import DocumentService from '@/services/DocumentService';
import Attachment from '@/ecm/models/search/results/list/Attachment';
import Utils from '@/ecm/common/Utils';
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import ContentActionType from '../../../../../../../ecm/models/ContentActionType';

export default {
    components: {
        VueDropzone: vue2Dropzone,
    },
    props: {
        contentTypeId: {
            type: Number,
        },
    },
    created() {
        DocumentService.getSupportedFileFormats().then((data) => {
            this.dropzoneOptions.acceptedFiles = data.join(',');
        });
    },
    data() {
        return {
            attachments: [],
            modal: {
                title: this.$t('DocumentAttachments'),
                key: 'attachmentModal',
            },
            open: false,
            dropZoneKey: 'drop-zone-attachment-modal',
            dropzoneOptions: {
                url: '',
                thumbnailWidth: 150,
                timeout: 30000, //30 seconds
                maxFilesize: 100, //100 MB
                uploadMultiple: true,
                parallelUploads: 10,
                maxFiles: 10,
                autoProcessQueue: true,
                destroyDropzone: true,
                headers: { Authorization: 'bearer ' + this.$store.getters['account/token'] },
                acceptedFiles: '',
                dictDefaultMessage: this.$t('DropzoneMessage'),
                dictRemoveFile: this.$t('Remove'),
                dictInvalidFileType: this.$t('DropzoneInvalidFileType'),
                previewTemplate: this.template(),
            },
            documentId: 0,
        };
    },
    mixins: [validationMixin],
    validations: {
        attachments: {
            $each: {
                description: { required },
            },
        },
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        dropZone() {
            return this.$refs[this.dropZoneKey];
        },
        isEditMode() {
            return this.attachments.some(t => t.id > 0);
        }
    },
    mounted() {
        this.updateDropzoneUrl();
    },
    methods: {
        getDescriptionErrors(index) {
            const errors = [];
            const validation = this.$v.attachments.$each[index];
            if (!validation || !validation.description.$dirty) return errors;
            if (!validation.description.required) {
                errors.push(this.$t('RequiredField'));
            }
            return errors;
        },
        updateDropzoneUrl() {
            if (!this.contentTypeId) return;
            const url = `${this.$store.getters['account/apiUrl']}/api/document/upload-attachment?contentTypeID=${this.contentTypeId}&contentActionType=${ContentActionType.AddAttachment}`;

            this.dropzoneOptions.url = url;

            this.$nextTick(() => {
                const dropzoneComponent = this.$refs[this.dropZoneKey];
                if (dropzoneComponent && dropzoneComponent.dropzone) {
                    dropzoneComponent.dropzone.options.url = url;
                }
            });
        },
        async onFileSuccess(file, response) {
            await this.attachments.map(async (a) => {
                if (a.fileName === file.name) {
                    const responseForThisFile = response.filter(
                        (r) => r.text == file.name
                    )[0];
                    a.fileId = responseForThisFile.id;
                    a.isLoading = false;
                }
            });
        },
        async onFileAdded(file) {
            const isDuplicate = this.attachments.some((attachment) => {
                return Utils.text.contains(attachment.fileName, file.name) > 0;
            });

            if (isDuplicate) {
                Utils.toast.error(this.$t('DocumentDuplicatedError'));
                this.dropZone.removeFile(file);
                return;
            }

            const extension = `.${file.name.split('.').pop()}`.toUpperCase();

            if (
                this.dropzoneOptions.acceptedFiles &&
                this.dropzoneOptions.acceptedFiles
                    .split(',')
                    .filter((f) => f === extension).length === 0
            ) {
                Utils.toast.error(this.$t('DropzoneInvalidFileType'));
                this.dropZone.removeFile(file);
                return;
            }

            const attachment = new Attachment();
            attachment.fileName = file.name;
            attachment.fileSize = file.size;
            attachment.description = file.name;
            attachment.isLoading = true;
            attachment.documentId = this.documentId;

            this.attachments = [...this.attachments, attachment];
        },
        remove(index) {
            const attachment = this.attachments[index];
            // Remove the matching file from the dropzone
            const dropzoneFiles = this.dropZone.getAcceptedFiles();
            const dzFile = dropzoneFiles.find((f) => f.name === attachment.fileName);
            if (dzFile) {
                this.dropZone.removeFile(dzFile);
            }
            // Remove from the attachments array
            this.attachments.splice(index, 1);
        },
        onFileError(file) {
            if (!file.accepted) {
                this.dropZone.removeFile(file);
            }
        },
        saveItem() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            if (this.attachments.length === 0) {
                Utils.toast.error(this.$t('DocumentAttachmentRequired'));
                return;
            }

            if (!this.isEditMode && !this.attachments.every(t => t.fileId)) {
                Utils.toast.error(this.$t('DocumentAttachmentRequired'));
                return;
            }

            this.modalInstance.progress(true);

            const finalAttachmentPayload = this.attachments.map((a) => {
                return {
                    ...a.toDto(),
                };
            });

            AttachmentService.save(finalAttachmentPayload)
                .then((result) => {
                    if (result) {
                        this.hide();
                        this.$emit('on-attachment-saved');
                    }
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.modalInstance.progress(false);
                });
        },
        new(documentId, files, contentTypeId) {
            this.$v.$reset();
            this.attachments = [];

            this.contentTypeId = contentTypeId;
            this.documentId = documentId;
            this.updateDropzoneUrl();

            this.$nextTick(() => {
                if (this.dropZone) {
                    this.dropZone.removeAllFiles();
                    if (files && files.length > 0) {
                        this.dropZone.dropzone.handleFiles(files);
                    }
                }
                this.show();
            });
        },
        edit(attachment) {
            this.attachments = [Utils.object.clone(new Attachment(), attachment)];
            this.$nextTick(() => {
                if (this.dropZone) {
                    this.dropZone.removeAllFiles();
                }
                this.show();
            });
        },
        show() {
            this.open = true;
        },
        hide() {
            this.open = false;
        },
        template: function () {
            return '<div class="dz-preview dz-file-preview"></div>';
        },
    },
};
</script>

<style>

#dropZoneAttachmentModal {
    min-height: 90px !important;
    margin-bottom: 5px !important;
}

#dropZoneAttachmentModal .dz-default.dz-message {
    display: block !important;
    margin: 1em 0 !important;
 }

 #dropZoneAttachmentModal  .dz-preview {
     display: none !important;
 }
</style>
